import {useMutation} from '@apollo/react-hooks';

import type {CoreEntityType} from 'nutshell-core/types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    CreateSavedFilter as CreateFilterMutation,
    CreateSavedFilterVariables as CreateFilterMutationVariables,
    FilterCreateInput,
    GetSavedFilters as GetSavedFiltersQuery,
} from 'nutshell-graphql-types';

import {getSavedFiltersReportType} from '../../entity-list-sidebar/helpers';

import CREATE_FILTER from '../mutations/create-saved-filter.graphql';
import GET_SAVED_FILTERS from '../queries/get-saved-filters.graphql';

export function useCreateFilter(
    entityType: CoreEntityType,
    searchNameQuery?: string | null
): {
    createFilter: (arg1: FilterCreateInput) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} {
    const [createFilter, {loading, error}] = useMutation<
        CreateFilterMutation,
        CreateFilterMutationVariables
    >(CREATE_FILTER);

    return {
        createFilter: (input: FilterCreateInput) =>
            createFilter({
                variables: {input},
                update: (proxy, mutationResults) => {
                    const cacheResult: GetSavedFiltersQuery | null | undefined = proxy.readQuery({
                        query: GET_SAVED_FILTERS,
                        variables: {
                            includeShared: false,
                            type: getSavedFiltersReportType(entityType),
                            searchNameQuery,
                        },
                    });

                    const newFilter =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.filterCreate &&
                        mutationResults.data.filterCreate.filter;

                    if (!newFilter) return;

                    if (cacheResult) {
                        const data = {
                            savedFilters: {
                                ...cacheResult.savedFilters,
                                edges: [
                                    {
                                        node: newFilter,
                                        __typename: 'SavedFiltersEdge',
                                    },
                                    // @ts-expect-error
                                ].concat(cacheResult.savedFilters.edges),
                            },
                        } as const;

                        proxy.writeQuery({
                            variables: {
                                includeShared: false,
                                type: getSavedFiltersReportType(entityType),
                                searchNameQuery,
                            },
                            query: GET_SAVED_FILTERS,
                            data: data,
                        });
                    }
                },
            }),
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
}
